import { useCallback, useState, ReactElement, ReactNode } from 'react'
import {
  Checkbox,
  FormControlLabel,
  Stack,
  styled,
  SxProps,
  Typography,
  TypographyProps,
} from '@mui/material'
import { useIsDesktopSize } from '$common/react-hooks/layout'
import { BottomDrawer } from '$common/bottom-drawer'
import { SettingsSection } from '$common/settings-section'
import { ErrorButton, SecondaryButton } from '$common/buttons'
import { nothing } from '$func/utils'
import { CancelOutlinedButton } from '$common/buttons/cancel-outlined-button'

const StyledBottomDrawer = styled(BottomDrawer)`
  & .MuiPaper-root {
    &[data-is-desktop='true'] {
      max-width: 450px;

      & .MuiFormControlLabel-root {
        margin: 16px 0 0;
      }

      & .MuiCheckbox-root {
        padding: 2px;
      }
    }
  }
`

const IconBox = styled('div')`
  display: flex;
  padding: 12px;
  justify-content: center;
  align-items: center;
  margin-bottom: 15px;
  margin-top: 16px;
`

const ButtonsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
  gap: 12px;
  & > button {
    height: 40px;
  }
  &[data-is-desktop='true'] {
    flex-direction: row-reverse;
    justify-content: space-between;
    margin-top: 24px;
    gap: 0;

    & > button {
      height: 48px;
      width: calc(50% - 6px);
    }
  }
`

const ButtonTypography = (props: TypographyProps) => {
  const isDesktopSize = useIsDesktopSize()
  return (
    <Typography
      variant={isDesktopSize ? 'subtitle1' : 'subtitle3'}
      color="inherit"
      {...props}
    />
  )
}

export interface ConfirmationDialogProps {
  icon?: ReactElement
  title?: string
  confirmOption: string
  cancelOption?: string
  checkboxOption?: string
  checkboxDefault?: boolean
  description?: JSX.Element | string
  alertMode?: boolean
  onConfirm: (checked?: boolean) => unknown
  onCancel?: () => unknown
  rootSx?: SxProps
  isOpen?: boolean
}

export function ConfirmationDialog({
  icon,
  title,
  description,
  confirmOption,
  cancelOption,
  onConfirm,
  onCancel,
  alertMode,
  checkboxOption,
  checkboxDefault,
  rootSx,
  isOpen,
}: ConfirmationDialogProps) {
  const isDesktopSize = useIsDesktopSize()
  const [checked, setChecked] = useState(checkboxDefault || false)

  const handleOnConfirm = useCallback(async () => {
    await onConfirm(checkboxOption ? checked : undefined)
  }, [checked, onConfirm])

  return (
    <StyledBottomDrawer
      isOpen={!!isOpen}
      onClose={onCancel || nothing}
      // @ts-ignore
      paperProps={{ 'data-is-desktop': isDesktopSize }}
    >
      {icon && <IconBox>{icon}</IconBox>}

      <SettingsSection
        headerText={title}
        headerVariant={isDesktopSize ? 'h3' : 'subtitle1'}
        headerColor={isDesktopSize ? 'neutral1000' : 'secondary700'}
        headerSx={isDesktopSize ? { margin: 3, marginBottom: 2 } : undefined}
        sx={{ marginBottom: 0, ...rootSx }}
      >
        <Stack
          sx={{
            padding: isDesktopSize ? '0 24px 24px' : '5px 20px 20px',
            boxSizing: 'border-box',
          }}
        >
          <Typography
            variant={isDesktopSize ? 'body1' : 'paragraph2'}
            dir="auto"
            component="pre"
            sx={{
              overflowWrap: 'break-word',
              wordWrap: 'break-word',
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              textAlign: 'right',
            }}
          >
            {description}
          </Typography>

          {checkboxOption && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={checked}
                  onChange={e => setChecked(e.target.checked)}
                  sx={{ color: 'neutral300' }}
                />
              }
              label={
                <Typography variant={isDesktopSize ? 'body1' : 'body2'}>
                  {checkboxOption}
                </Typography>
              }
            />
          )}

          <ButtonsContainer data-is-desktop={isDesktopSize}>
            {alertMode ? (
              <ErrorButton
                text={<ButtonTypography>{confirmOption}</ButtonTypography>}
                onClick={handleOnConfirm}
              />
            ) : (
              <SecondaryButton
                text={<ButtonTypography>{confirmOption}</ButtonTypography>}
                onClick={handleOnConfirm}
              />
            )}

            {cancelOption && (
              <CancelOutlinedButton
                text={<ButtonTypography>{cancelOption}</ButtonTypography>}
                onClick={onCancel || nothing}
              />
            )}
          </ButtonsContainer>
        </Stack>
      </SettingsSection>
    </StyledBottomDrawer>
  )
}

ConfirmationDialog.defaultProps = {
  icon: null,
  title: '',
  cancelOption: '',
  onCancel: nothing,
  checkboxOption: '',
  description: '',
  alertMode: false,
  checkboxDefault: false,
  rootSx: null,
}
